import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  getGiftCards,
  getListingsById,
  getListingsByIdBeforeAfters,
  getListingsByIdExpectations,
  getListingsByIdMedia,
  getListingsByIdPackages,
  getListingsByIdRelated,
  getMembershipsByMembershipId,
  getMembershipsByMembershipIdRewards,
  getMultiPlatformPlatformByPlatformIdLocations,
  getPlatform,
  getPlatformUserMemberships,
  getRetailproductsByRetailProductListingIdShopProductListingDetails,
  getShopDiscountsListingsByListingId,
} from "api/methods";
import { Models } from "api/types";
import { STALE_TIME } from "api/staleTimes";

export const usePrefetchPDP = () => {
  const queryClient = useQueryClient();

  const prefetchPackagePDP = useCallback(
    (listingId: string) => {
      queryClient.prefetchQuery({
        ...getListingsById.keyed({ id: listingId }),
        staleTime: STALE_TIME.catalog,
      });
      queryClient.prefetchQuery({
        ...getPlatformUserMemberships.keyed(),
        staleTime: 1000 * 60 * 2,
      });
      const platform = queryClient.getQueryData<Models<"PlatformModel">>(
        getPlatform.keyed().queryKey,
      );
      if (platform?.id) {
        queryClient.prefetchQuery({
          ...getMultiPlatformPlatformByPlatformIdLocations.keyed({ platformId: platform.id }),
          staleTime: STALE_TIME.platformConfig,
        });
      }

      queryClient.prefetchQuery({
        ...getListingsByIdBeforeAfters.keyed({ id: listingId }),
        staleTime: STALE_TIME.catalog,
      });

      queryClient.prefetchQuery({
        ...getListingsByIdExpectations.keyed({ id: listingId }),
        staleTime: STALE_TIME.catalog,
      });

      queryClient.prefetchQuery({
        ...getListingsByIdMedia.keyed({ id: listingId }),
        staleTime: STALE_TIME.catalog,
      });

      queryClient.prefetchQuery({
        ...getListingsByIdPackages.keyed({ id: listingId }),
        staleTime: STALE_TIME.catalog,
      });

      queryClient.prefetchQuery({
        ...getListingsByIdRelated.keyed({ id: listingId }),
        staleTime: STALE_TIME.catalog,
      });

      queryClient.prefetchQuery({
        ...getShopDiscountsListingsByListingId.keyed({ listingId }),
        staleTime: 1000 * 60 * 5,
      });
    },
    [queryClient],
  );

  const prefetchMembershipPDP = useCallback(
    (membershipId: string) => {
      queryClient.prefetchQuery({
        ...getMembershipsByMembershipId.keyed({ membershipId }),
        staleTime: STALE_TIME.catalog,
      });

      queryClient.prefetchQuery({
        ...getMembershipsByMembershipIdRewards.keyed({ membershipId }),
        staleTime: STALE_TIME.catalog,
      });
    },
    [queryClient],
  );

  const prefetchProductPDP = useCallback(
    (productId: string) => {
      queryClient.prefetchQuery({
        ...getRetailproductsByRetailProductListingIdShopProductListingDetails.keyed({
          retailProductListingId: productId,
        }),
      });
      void import("pages/root/shop/ProductPDP/ProductPDP").catch(() => undefined);
    },
    [queryClient],
  );

  const prefetchGiftCards = useCallback(() => {
    queryClient.prefetchQuery({ ...getGiftCards.keyed({}) });
  }, [queryClient]);

  return {
    prefetchPackagePDP,
    prefetchMembershipPDP,
    prefetchProductPDP,
    prefetchGiftCards,
  };
};
